import type { ScoreMetricResult } from '@/types/gmb';

// UI-2A｜4 大診斷構面 grouping（純顯示層）。
// 只加總「既有 Backend Metric」的 score / maxScore，不重算 threshold、不重新 normalize、
// 不建立第二套評分、不影響 Score / Grade / Coverage / Priority。

export interface DiagnosisDimension {
  key: string;
  title: string;
  caption: string;
  /** 已評分指標的得分加總（未評分指標不計入，不視為 0） */
  earned: number;
  /** 已評分指標的可用滿分加總 */
  availableMax: number;
  /** earned / availableMax（availableMax = 0 時為 0） */
  completionRate: number;
  /** 是否含資料不足 / 不適用指標 */
  hasInsufficientData: boolean;
  /** 是否含同業樣本不足指標 */
  hasInsufficientPeerData: boolean;
  scoredCount: number;
  metricCount: number;
}

export interface DimensionDef {
  key: string;
  title: string;
  caption: string;
  metricKeys: string[];
}

export const DIMENSION_DEFS: DimensionDef[] = [
  {
    key: 'A',
    title: '商家基礎完整度',
    caption: '基本營業與分類資料是否完整。',
    metricKeys: ['M01', 'M02', 'M03'],
  },
  {
    key: 'B',
    title: '接單與聯絡準備度',
    caption: '顧客是否容易聯絡並採取下一步。',
    metricKeys: ['M04', 'M05', 'M06'],
  },
  {
    key: 'C',
    title: '內容與信譽基礎',
    caption: '公開內容是否足以建立第一印象。',
    metricKeys: ['M07', 'M08', 'M09'],
  },
  {
    key: 'D',
    title: '同業競爭表現',
    caption: '評分與評論累積相對附近同業的表現。',
    metricKeys: ['M10', 'M11'],
  },
];

/**
 * 依既有 MetricResult 產生 4 大構面。
 * 未評分（INSUFFICIENT_DATA / INSUFFICIENT_PEER_DATA / NOT_APPLICABLE）的指標
 * 不計入分子與分母，因此不會被當成 0 分。
 */
export function buildDiagnosisDimensions(metrics: ScoreMetricResult[]): DiagnosisDimension[] {
  const map = new Map<string, ScoreMetricResult>();
  (metrics || []).forEach((m) => map.set(m.key, m));

  return DIMENSION_DEFS.map((def) => {
    let earned = 0;
    let availableMax = 0;
    let hasInsufficientData = false;
    let hasInsufficientPeerData = false;
    let scoredCount = 0;

    def.metricKeys.forEach((key) => {
      const metric = map.get(key);
      if (!metric) return;
      if (metric.status === 'SCORED' && typeof metric.score === 'number') {
        earned += metric.score;
        availableMax += metric.maxScore;
        scoredCount += 1;
        return;
      }
      if (metric.status === 'INSUFFICIENT_PEER_DATA') {
        hasInsufficientPeerData = true;
        return;
      }
      // INSUFFICIENT_DATA / NOT_APPLICABLE
      hasInsufficientData = true;
    });

    return {
      key: def.key,
      title: def.title,
      caption: def.caption,
      earned,
      availableMax,
      completionRate: availableMax > 0 ? earned / availableMax : 0,
      hasInsufficientData,
      hasInsufficientPeerData,
      scoredCount,
      metricCount: def.metricKeys.length,
    };
  });
}

/**
 * 最大缺口 = 4 大構面中「完成率最低」者（僅在可用滿分 > 0 的構面中挑選）。
 * 只用於 Dashboard 顯示，不影響 Score / Priority / Grade。
 */
export function resolveMaxGapDimension(dimensions: DiagnosisDimension[]): DiagnosisDimension | null {
  const eligible = (dimensions || []).filter((d) => d.availableMax > 0);
  if (eligible.length === 0) return null;
  return eligible.reduce(
    (lowest, current) => (current.completionRate < lowest.completionRate ? current : lowest),
    eligible[0],
  );
}

/**
 * 依指標 key 找出所屬構面（供「為什麼重要」文案與 Metrics 分組使用；純顯示）。
 */
export function findDimensionForMetric(metricKey: string): DimensionDef | null {
  return DIMENSION_DEFS.find((def) => def.metricKeys.includes(metricKey)) ?? null;
}